import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Paper,
  Group,
  Text,
  Badge,
  SimpleGrid,
  Progress,
  ThemeIcon,
  Tabs,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import {
  IconServer,
  IconDatabase,
  IconCpu,
  IconActivity,
  IconAlertTriangle,
  IconAlertCircle,
  IconInfoCircle,
  IconChartLine,
} from '@tabler/icons-react';
import { formatDateTime } from '../utils/date';
import { PageContainer } from '../components/PageContainer';
import { BackButton } from '../components/BackButton';
import { monitoringApi, SystemOverview, ServerMetrics, Alert as MonitoringAlert } from '../api/monitoring';

export function MonitoringPage() {
  const [activeTab, setActiveTab] = useState<string>('overview');

  const { data: overview, isLoading: overviewLoading, dataUpdatedAt } = useQuery({
    queryKey: ['monitoring', 'overview'],
    queryFn: monitoringApi.getOverview,
    refetchInterval: 30000,
  });

  const { data: server, isLoading: serverLoading } = useQuery({
    queryKey: ['monitoring', 'server'],
    queryFn: monitoringApi.getServerMetrics,
    refetchInterval: 30000,
  });

  const { data: dbMetrics } = useQuery({
    queryKey: ['monitoring', 'database'],
    queryFn: monitoringApi.getDatabaseMetrics,
    refetchInterval: 30000,
  });

  const { data: redisMetrics } = useQuery({
    queryKey: ['monitoring', 'redis'],
    queryFn: monitoringApi.getRedisMetrics,
    refetchInterval: 30000,
  });

  if (overviewLoading || serverLoading) {
    return (
      <PageContainer>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Stack gap="md">
        <Group gap="xs">
          <BackButton />
          <Title order={3}>Мониторинг</Title>
          {overview && (
            <Badge color={getStatusColor(overview.status)} size="lg">
              {overview.status === 'healthy' ? 'Работает' : overview.status === 'degraded' ? 'Деградация' : 'Проблемы'}
            </Badge>
          )}
        </Group>
        
        {dataUpdatedAt && (
          <Text size="xs" c="dimmed">
            Обновлено: {formatDateTime(new Date(dataUpdatedAt))}
          </Text>
        )}

        {overview && overview.critical_alerts > 0 && (
          <Alert icon={<IconAlertTriangle size={20} />} color="red" title="Критические алерты">
            {overview.critical_alerts} критических проблем требуют внимания
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(v) => setActiveTab(v || 'overview')}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartLine size={16} />}>Обзор</Tabs.Tab>
            <Tabs.Tab value="server" leftSection={<IconServer size={16} />}>Сервер</Tabs.Tab>
            <Tabs.Tab value="database" leftSection={<IconDatabase size={16} />}>База данных</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            {overview && <OverviewPanel overview={overview} />}
          </Tabs.Panel>

          <Tabs.Panel value="server" pt="md">
            {server && <ServerPanel server={server} />}
          </Tabs.Panel>

          <Tabs.Panel value="database" pt="md">
            <DatabasePanel dbMetrics={dbMetrics} redisMetrics={redisMetrics} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </PageContainer>
  );
}

function OverviewPanel({ overview }: { overview: SystemOverview }) {
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <MetricCard
          title="CPU"
          value={`${overview.cpu_percent.toFixed(1)}%`}
          progress={overview.cpu_percent}
          icon={IconCpu}
          color={overview.cpu_percent > 80 ? 'red' : overview.cpu_percent > 60 ? 'yellow' : 'blue'}
        />
        <MetricCard
          title="Память"
          value={`${overview.memory_percent.toFixed(1)}%`}
          progress={overview.memory_percent}
          icon={IconActivity}
          color={overview.memory_percent > 90 ? 'red' : overview.memory_percent > 70 ? 'yellow' : 'green'}
        />
        <MetricCard
          title="Диск"
          value={`${overview.disk_percent.toFixed(1)}%`}
          progress={overview.disk_percent}
          icon={IconServer}
          color={overview.disk_percent > 85 ? 'red' : overview.disk_percent > 70 ? 'yellow' : 'orange'}
        />
        <MetricCard
          title="Алерты"
          value={overview.alerts_count > 0 ? overview.alerts_count.toString() : '-'}
          subtitle={overview.critical_alerts > 0 ? `${overview.critical_alerts} критических` : undefined}
          icon={IconAlertTriangle}
          color={overview.critical_alerts > 0 ? 'red' : overview.alerts_count > 0 ? 'yellow' : 'green'}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <Paper p="md" withBorder>
          <Group mb="xs">
            <ThemeIcon size="md" variant="light" color="blue">
              <IconDatabase size={16} />
            </ThemeIcon>
            <Text fw={500}>PostgreSQL</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Статус:</Text>
            <Badge color={overview.db_status === 'connected' ? 'green' : 'red'} size="xs">
              {translateStatus(overview.db_status)}
            </Badge>
          </Group>
          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">Соединений:</Text>
            <Text size="sm" fw={500}>{overview.db_connections}</Text>
          </Group>
        </Paper>

        <Paper p="md" withBorder>
          <Group mb="xs">
            <ThemeIcon size="md" variant="light" color="red">
              <IconDatabase size={16} />
            </ThemeIcon>
            <Text fw={500}>Redis</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Статус:</Text>
            <Badge color={overview.redis_status === 'connected' ? 'green' : 'red'} size="xs">
              {translateStatus(overview.redis_status)}
            </Badge>
          </Group>
          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">Ключей:</Text>
            <Text size="sm" fw={500}>{overview.redis_keys}</Text>
          </Group>
          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">Память:</Text>
            <Text size="sm" fw={500}>{overview.redis_memory_mb.toFixed(1)} MB</Text>
          </Group>
        </Paper>

        <Paper p="md" withBorder>
          <Group mb="xs">
            <ThemeIcon size="md" variant="light" color="violet">
              <IconActivity size={16} />
            </ThemeIcon>
            <Text fw={500}>MQTT</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Статус:</Text>
            <Badge color={overview.mqtt_status === 'connected' ? 'green' : 'red'} size="xs">
              {translateStatus(overview.mqtt_status)}
            </Badge>
          </Group>
        </Paper>

        <Paper p="md" withBorder>
          <Group mb="xs">
            <ThemeIcon size="md" variant="light" color="cyan">
              <IconServer size={16} />
            </ThemeIcon>
            <Text fw={500}>Nginx</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Статус:</Text>
            <Badge color={overview.nginx_status === 'online' ? 'green' : 'red'} size="xs">
              {translateStatus(overview.nginx_status || 'unknown')}
            </Badge>
          </Group>
          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">Соединений:</Text>
            <Text size="sm" fw={500}>{overview.nginx_active_connections || 0}</Text>
          </Group>
          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">Запросов:</Text>
            <Text size="sm" fw={500}>{overview.nginx_requests || 0}</Text>
          </Group>
        </Paper>
      </SimpleGrid>

      {overview.recent_alerts && overview.recent_alerts.length > 0 && (
        <>
          <Title order={5}>Последние алерты</Title>
          <Stack gap="xs">
            {overview.recent_alerts.slice(0, 5).map((alert) => (
              <AlertCard key={alert.id} alert={alert} />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}

function ServerPanel({ server }: { server: ServerMetrics }) {
  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Group>
            <ThemeIcon size="xl" variant="light" color={getStatusColor(server.status)}>
              <IconServer size={24} />
            </ThemeIcon>
            <div>
              <Text fw={600}>{server.server_name}</Text>
              <Text size="sm" c="dimmed">{server.ip}</Text>
            </div>
          </Group>
          <Badge size="lg" color={getStatusColor(server.status)}>{translateStatus(server.status)}</Badge>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <MetricCard
          title="CPU"
          value={`${server.system_cpu_percent.toFixed(1)}%`}
          progress={server.system_cpu_percent}
          icon={IconCpu}
          color={server.system_cpu_percent > 80 ? 'red' : 'blue'}
        />
        <MetricCard
          title="Память"
          value={`${server.system_memory_percent.toFixed(1)}%`}
          subtitle={`${(server.system_memory_used_mb / 1024).toFixed(1)} / ${(server.system_memory_total_mb / 1024).toFixed(1)} GB`}
          progress={server.system_memory_percent}
          icon={IconActivity}
          color={server.system_memory_percent > 90 ? 'red' : 'green'}
        />
        <MetricCard
          title="Диск"
          value={`${server.system_disk_percent.toFixed(1)}%`}
          subtitle={`${server.system_disk_used_gb.toFixed(1)} / ${server.system_disk_total_gb.toFixed(1)} GB`}
          progress={server.system_disk_percent}
          icon={IconServer}
          color={server.system_disk_percent > 85 ? 'red' : 'orange'}
        />
        <MetricCard
          title="Load"
          value={server.system_load_1m.toFixed(2)}
          subtitle={`5m: ${server.system_load_5m.toFixed(2)} | 15m: ${server.system_load_15m.toFixed(2)}`}
          icon={IconChartLine}
          color="violet"
        />
      </SimpleGrid>

      <Title order={5}>Приложение</Title>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Версия:</Text>
              <Text size="sm" fw={500}>{server.app_version}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">PID:</Text>
              <Text size="sm" fw={500}>{server.app_pid}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Память:</Text>
              <Text size="sm" fw={500}>{server.app_memory_mb.toFixed(0)} MB</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">CPU:</Text>
              <Text size="sm" fw={500}>{server.app_cpu_percent.toFixed(1)}%</Text>
            </Group>
          </Stack>
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}

function DatabasePanel({ dbMetrics, redisMetrics }: { dbMetrics?: any; redisMetrics?: any }) {
  return (
    <Stack gap="md">
      <Title order={5}>PostgreSQL</Title>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Paper p="md" withBorder>
          <Group mb="sm">
            <ThemeIcon size="md" variant="light" color="blue">
              <IconDatabase size={16} />
            </ThemeIcon>
            <Text fw={500}>Соединения</Text>
          </Group>
          {dbMetrics ? (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Статус:</Text>
                <Badge color={dbMetrics.status === 'connected' ? 'green' : 'red'} size="xs">{translateStatus(dbMetrics.status)}</Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Открытых:</Text>
                <Text size="sm" fw={500}>{dbMetrics.open_connections}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Активных:</Text>
                <Text size="sm" fw={500}>{dbMetrics.in_use}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Простаивает:</Text>
                <Text size="sm" fw={500}>{dbMetrics.idle}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Всего на сервере:</Text>
                <Text size="sm" fw={500}>{dbMetrics.total_connections || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Активных запросов:</Text>
                <Text size="sm" fw={500}>{dbMetrics.active_queries || 0}</Text>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Загрузка...</Text>
          )}
        </Paper>

        <Paper p="md" withBorder>
          <Group mb="sm">
            <ThemeIcon size="md" variant="light" color="blue">
              <IconActivity size={16} />
            </ThemeIcon>
            <Text fw={500}>Производительность</Text>
          </Group>
          {dbMetrics ? (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Cache Hit:</Text>
                <Text size="sm" fw={500}>{dbMetrics.cache_hit_ratio?.toFixed(1) || 0}%</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Транзакций:</Text>
                <Text size="sm" fw={500}>{(dbMetrics.transactions_committed || 0).toLocaleString()}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Откатов:</Text>
                <Text size="sm" fw={500}>{dbMetrics.transactions_rolled_back || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Deadlocks:</Text>
                <Text size="sm" fw={500}>{dbMetrics.deadlocks || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Temp файлов:</Text>
                <Text size="sm" fw={500}>{dbMetrics.temp_files || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Uptime:</Text>
                <Text size="sm" fw={500}>{dbMetrics.uptime || '-'}</Text>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Загрузка...</Text>
          )}
        </Paper>

        <Paper p="md" withBorder>
          <Group mb="sm">
            <ThemeIcon size="md" variant="light" color="blue">
              <IconServer size={16} />
            </ThemeIcon>
            <Text fw={500}>Хранилище</Text>
          </Group>
          {dbMetrics ? (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Размер БД:</Text>
                <Text size="sm" fw={500}>{dbMetrics.database_size_mb?.toFixed(1) || 0} MB</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Таблиц:</Text>
                <Text size="sm" fw={500}>{dbMetrics.tables_count || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Индексов:</Text>
                <Text size="sm" fw={500}>{dbMetrics.indexes_count || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Строк:</Text>
                <Text size="sm" fw={500}>{(dbMetrics.total_rows || 0).toLocaleString()}</Text>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Загрузка...</Text>
          )}
        </Paper>
      </SimpleGrid>

      <Title order={5}>Redis</Title>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Paper p="md" withBorder>
          <Group mb="sm">
            <ThemeIcon size="md" variant="light" color="red">
              <IconDatabase size={16} />
            </ThemeIcon>
            <Text fw={500}>Сервер</Text>
          </Group>
          {redisMetrics ? (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Статус:</Text>
                <Badge color={redisMetrics.status === 'connected' ? 'green' : 'red'} size="xs">{translateStatus(redisMetrics.status)}</Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Версия:</Text>
                <Text size="sm" fw={500}>{redisMetrics.redis_version || '-'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Роль:</Text>
                <Text size="sm" fw={500}>{redisMetrics.role || '-'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Uptime:</Text>
                <Text size="sm" fw={500}>{redisMetrics.uptime_days || 0} дней</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Клиентов:</Text>
                <Text size="sm" fw={500}>{redisMetrics.connected_clients || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Заблокировано:</Text>
                <Text size="sm" fw={500}>{redisMetrics.blocked_clients || 0}</Text>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Загрузка...</Text>
          )}
        </Paper>

        <Paper p="md" withBorder>
          <Group mb="sm">
            <ThemeIcon size="md" variant="light" color="red">
              <IconActivity size={16} />
            </ThemeIcon>
            <Text fw={500}>Память</Text>
          </Group>
          {redisMetrics ? (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Используется:</Text>
                <Text size="sm" fw={500}>{redisMetrics.used_memory_mb?.toFixed(2) || 0} MB</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Пик:</Text>
                <Text size="sm" fw={500}>{redisMetrics.used_memory_peak_mb?.toFixed(2) || 0} MB</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">RSS:</Text>
                <Text size="sm" fw={500}>{redisMetrics.used_memory_rss_mb?.toFixed(2) || 0} MB</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Фрагментация:</Text>
                <Text size="sm" fw={500}>{redisMetrics.mem_fragmentation_ratio?.toFixed(2) || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Ключей:</Text>
                <Text size="sm" fw={500}>{redisMetrics.keys || 0}</Text>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Загрузка...</Text>
          )}
        </Paper>

        <Paper p="md" withBorder>
          <Group mb="sm">
            <ThemeIcon size="md" variant="light" color="red">
              <IconChartLine size={16} />
            </ThemeIcon>
            <Text fw={500}>Статистика</Text>
          </Group>
          {redisMetrics ? (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Ops/sec:</Text>
                <Text size="sm" fw={500}>{redisMetrics.instantaneous_ops_per_sec || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Hit Rate:</Text>
                <Text size="sm" fw={500}>{redisMetrics.hit_rate?.toFixed(1) || 0}%</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Команд:</Text>
                <Text size="sm" fw={500}>{(redisMetrics.total_commands_processed || 0).toLocaleString()}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Соединений:</Text>
                <Text size="sm" fw={500}>{(redisMetrics.total_connections_received || 0).toLocaleString()}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Expired:</Text>
                <Text size="sm" fw={500}>{redisMetrics.expired_keys || 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Evicted:</Text>
                <Text size="sm" fw={500}>{redisMetrics.evicted_keys || 0}</Text>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Загрузка...</Text>
          )}
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  progress,
  icon: Icon,
  color,
}: {
  title: string;
  value: string;
  subtitle?: string;
  progress?: number;
  icon: any;
  color: string;
}) {
  return (
    <Paper p="md" withBorder>
      <Group mb="xs">
        <ThemeIcon size="sm" variant="light" color={color}>
          <Icon size={16} />
        </ThemeIcon>
        <Text size="sm" c="dimmed">{title}</Text>
      </Group>
      <Text size="lg" fw={600}>{value}</Text>
      {subtitle && <Text size="xs" c="dimmed">{subtitle}</Text>}
      {progress !== undefined && <Progress value={progress} color={color} size="sm" mt="xs" />}
    </Paper>
  );
}

function AlertCard({ alert }: { alert: MonitoringAlert }) {
  const getIcon = () => {
    switch (alert.severity) {
      case 'critical':
      case 'error':
        return <IconAlertCircle size={16} />;
      case 'warning':
        return <IconAlertTriangle size={16} />;
      default:
        return <IconInfoCircle size={16} />;
    }
  };

  const getColor = () => {
    switch (alert.severity) {
      case 'critical':
        return 'red';
      case 'error':
        return 'orange';
      case 'warning':
        return 'yellow';
      default:
        return 'blue';
    }
  };

  return (
    <Alert icon={getIcon()} color={getColor()} variant="light">
      <Text size="sm">{alert.message}</Text>
      <Text size="xs" c="dimmed">{formatDateTime(new Date(alert.timestamp))}</Text>
    </Alert>
  );
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'online':
    case 'healthy':
    case 'connected':
      return 'green';
    case 'degraded':
      return 'yellow';
    case 'offline':
    case 'unhealthy':
    case 'disconnected':
    case 'error':
      return 'red';
    default:
      return 'gray';
  }
}

function translateStatus(status: string): string {
  const translations: Record<string, string> = {
    'connected': 'Подключен',
    'disconnected': 'Отключен',
    'online': 'Онлайн',
    'offline': 'Офлайн',
    'healthy': 'Работает',
    'unhealthy': 'Проблемы',
    'degraded': 'Деградация',
    'error': 'Ошибка',
    'unknown': 'Неизвестно',
  };
  return translations[status] || status;
}

export default MonitoringPage;
